import React, { useEffect, useRef, useState } from 'react';
import MixPanel from 'mixpanel-browser';
import { useDispatch, useSelector } from 'react-redux';
import { LoginActions } from 'Redux/modules/login/actions';
import { LoginSelectors } from 'Redux/modules/login/selectors';
import { TWOFA_EVENT_PREFIX } from 'Redux/modules/login/constants';
import styles from './LoginPage.module.less';

const CODE_LENGTH = 6;

const TwoFactorPanel = () => {
  const dispatch = useDispatch();

  const challengeId = useSelector(LoginSelectors.loginChallengeIdSelector);
  const twoFaEmail = useSelector(LoginSelectors.loginTwoFaEmailSelector);
  const twoFaTriggers = useSelector(LoginSelectors.loginTwoFaTriggersSelector);
  const resendAvailableIn = useSelector(LoginSelectors.loginResendAvailableInSelector);
  const resendNonce = useSelector(LoginSelectors.loginResendNonceSelector);
  const attemptsRemaining = useSelector(LoginSelectors.loginTwoFaAttemptsRemainingSelector);
  const twoFaError = useSelector(LoginSelectors.loginTwoFaErrorSelector);
  const isVerifying = useSelector(LoginSelectors.loginTwoFaVerifyingSelector);
  const isResending = useSelector(LoginSelectors.loginTwoFaResendingSelector);

  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(resendAvailableIn || 0);
  const tickRef = useRef(null);
  const inputRef = useRef(null);
  const shownTrackedRef = useRef(false);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    if (shownTrackedRef.current) return;
    shownTrackedRef.current = true;
    try {
      // `triggers` attributes the challenge (score / stale / api_failure); userEmail matches the
      // standard Mixpanel field the Trust console enriches events with.
      MixPanel.track(`${TWOFA_EVENT_PREFIX}2FA Shown`, { triggers: twoFaTriggers, userEmail: twoFaEmail });
    } catch (e) {
      // analytics must never break the auth flow
    }
  }, [twoFaTriggers, twoFaEmail]);

  // Restart the resend countdown whenever the backend hands us a new window. Keyed on
  // resendNonce too, so a resend that returns the same resend_available_in (e.g. 60 -> 60)
  // still restarts the counter instead of leaving it stuck at 0.
  useEffect(() => {
    setCountdown(resendAvailableIn || 0);
  }, [resendAvailableIn, resendNonce]);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    tickRef.current = setTimeout(() => setCountdown((s) => s - 1), 1000);
    return () => clearTimeout(tickRef.current);
  }, [countdown]);

  const handleCodeChange = (e) => {
    const digits = (e.target.value || '').replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
  };

  const handleVerify = (e) => {
    e.preventDefault();
    if (isVerifying || code.length !== CODE_LENGTH) return;
    dispatch(LoginActions.verify2fa({ challengeId, code }));
  };

  const handleResend = () => {
    if (isResending || countdown > 0) return;
    dispatch(LoginActions.resend2fa({ challengeId }));
  };

  // Show the wrong-code message only when we have an attempt count; other errors are toasted
  // by the saga (and send the user back to the password form).
  const showAttemptsError = twoFaError === 'invalid_code' && attemptsRemaining !== null;

  const resendLabel = () => {
    if (isResending) return 'Sending…';
    if (countdown > 0) return `Resend code in ${countdown}s`;
    return 'Resend code';
  };

  return (
    <form id="two-factor-form" onSubmit={handleVerify} autoCapitalize="off" autoCorrect="off">
      <div className={`${styles.loginHeader} loginHeader`}>Enter your code</div>
      <div className={styles['form-group']}>
        <span className={styles.twofaSubtitle}>We emailed a code to your address. It expires in 10 minutes.</span>
      </div>

      <div className={styles['form-group']}>
        <input
          id="id_2fa_code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          placeholder="6-digit code"
          className={styles['form-control']}
          value={code}
          onChange={handleCodeChange}
          ref={inputRef}
        />
      </div>

      {showAttemptsError && (
        <div id="twofa-error-msg" className={`${styles['form-group']} ${styles['text-danger']}`}>
          <span>
            Incorrect code. {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} left.
          </span>
        </div>
      )}

      <button
        type="submit"
        id="submit-2fa"
        className={`${styles.loginButton} ${styles.btn}`}
        disabled={isVerifying || code.length !== CODE_LENGTH}
      >
        {isVerifying ? 'Verifying…' : 'Verify'}
      </button>

      <div id="twofa-options" className={`${styles['form-group']} ${styles.idRemember}`}>
        <button
          type="button"
          id="resend-2fa"
          className={`${styles.signinLink} ${styles.linkButton}`}
          onClick={handleResend}
          disabled={isResending || countdown > 0}
        >
          {resendLabel()}
        </button>
      </div>
    </form>
  );
};

export default TwoFactorPanel;
